import React, {useContext, useEffect, useState} from "react";
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Switch,
  TextField,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import {StoreContext} from "../../../../common/struct/store";
import {AppContext} from "../../../../App";
import {isSuperAdmin} from "../../../../common/struct/globalVar";
import CloseIcon from "@mui/icons-material/Close";
import {useTranslation} from "react-i18next";
import moment from "moment";
import {TOperationParticipant} from "../../../../interfaces/operationParticipant";
import {TEmailTemplate} from "../../../../interfaces/email";
import {PrimaryBigButton, SecondaryBigButton} from "../../../../components/buttons/mainButton";
import {fr} from "date-fns/locale";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {DatePicker, TimePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {getCustomTemplatesForOperation} from "../../../../services/AdminService";

type SendEmailToParticipantProps = {
  openDialog: boolean,
  loading: boolean,
  closeDialog: () => void,
  sendEmail: (templateId: string | null, sendAt: string, manualIds?: string[]) => void,
  operationParticipant: TOperationParticipant | null,
  operationId?: string,
  participantCount?: number,
  manualIdsMode?: boolean,
  titleKey?: string,
  recipientInfoKey?: string,
  showTemplateSelection?: boolean,
  presetTemplateId?: string | null,
}

export default function SendEmailToParticipant(props: SendEmailToParticipantProps): JSX.Element {
  const {
    openDialog,
    loading,
    closeDialog,
    sendEmail,
    operationParticipant,
    operationId,
    participantCount,
    manualIdsMode,
    titleKey,
    recipientInfoKey,
    showTemplateSelection = true,
    presetTemplateId,
  } = props;
  const {t} = useTranslation();
  const [store] = useContext<StoreContext>(AppContext);
  const premiumLocked = !store.global.company.templateCreationActivated && !isSuperAdmin();

  const [templates, setTemplates] = useState<TEmailTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<TEmailTemplate | null>(null);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [sendAtDate, setSendAtDate] = useState<Date>(moment(new Date()).add(1, "day").toDate());
  const [sendAtTime, setSendAtTime] = useState<Date>(moment().set("hour", 9).set("minute", 0).toDate());
  const [useCustomTemplate, setUseCustomTemplate] = useState(false);
  const [manualIdsInput, setManualIdsInput] = useState("");

  useEffect(() => {
    const effectiveOperationId = operationId ?? operationParticipant?.operation.id;
    if (!openDialog || !effectiveOperationId) return;
    setSelectedTemplate(null);
    setScheduleEnabled(false);
    if (premiumLocked) {
      setTemplates([]);
      return;
    }
    setLoadingTemplates(true);
    setUseCustomTemplate(false);
    setManualIdsInput("");
    getCustomTemplatesForOperation(effectiveOperationId)
      .then(list => {
        setTemplates(list);
        if (presetTemplateId) {
          const preset = list.find(tpl => tpl.id === presetTemplateId) ?? null;
          setUseCustomTemplate(preset !== null);
          setSelectedTemplate(preset);
        }
      })
      .finally(() => setLoadingTemplates(false));
  }, [openDialog, presetTemplateId]);

  const getCombinedSendAt = (): string =>
    moment(sendAtDate)
      .set("hour", moment(sendAtTime).hour())
      .set("minute", moment(sendAtTime).minute())
      .format("YYYY-MM-DD HH:mm");

  const parsedManualIds = Array.from(
    new Set(manualIdsInput.split(/[\s,;]+/).map(id => id.trim()).filter(Boolean))
  );

  const NUM_ID_REGEX = /^\d+$/;
  const invalidManualIds = parsedManualIds.filter(id => !NUM_ID_REGEX.test(id));

  const rawManualIdsCount = manualIdsInput.split(/[\s,;]+/).map(id => id.trim()).filter(Boolean).length;
  const duplicateCount = rawManualIdsCount - parsedManualIds.length;

  const onConfirm = (): void => {
    sendEmail(
      useCustomTemplate ? selectedTemplate?.id ?? null : null,
      scheduleEnabled ? getCombinedSendAt() : "now",
      parsedManualIds.length > 0 ? parsedManualIds : undefined
    );
    closeDialog();
  };

  return (
    <Dialog open={openDialog} onClose={closeDialog} sx={{display: "flex", justifyContent: "center"}} disableEscapeKeyDown>
      <DialogTitle sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
        <Box sx={{display: "flex", alignItems: "center"}}>
          <Typography variant="h2" color="neutral.dark" sx={{fontWeight: "bold"}}>
            {titleKey
              ? t(titleKey)
              : operationParticipant
                ? t("participants.send_email.title")
                : manualIdsMode
                  ? t("participants.send_email.title_manual_ids")
                  : t("participants.send_email.title_selected_participants")}
          </Typography>
        </Box>
        <IconButton onClick={closeDialog}>
          <CloseIcon sx={{color: "neutral.main"}} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{pb: 5, minWidth: 480}}>
        <Box sx={{display: "flex", flexDirection: "column", gap: 4}}>
          {loadingTemplates ? (
            <CircularProgress size={20} />
          ) : (
            <Box sx={{display: "flex", flexDirection: "column", gap: 4}}>
              {showTemplateSelection && (
                <>
                  <RadioGroup value={useCustomTemplate ? "custom" : "invitation"} sx={{flexDirection: "row", gap: 2}}>
                    <FormControlLabel
                      value="invitation"
                      control={<Radio sx={{p: 0, fontSize: 24}} />}
                      onChange={(): void => setUseCustomTemplate(false)}
                      disabled={loading}
                      sx={{flex: 1, m: 0}}
                      label={<Typography variant="body2">{t("participants.send_email.template_invitation")}</Typography>}
                    />
                    {templates.length > 0 && (
                      <FormControlLabel
                        value="custom"
                        control={<Radio sx={{p: 0, fontSize: 24}} />}
                        onChange={(): void => setUseCustomTemplate(true)}
                        disabled={loading}
                        sx={{flex: 1, m: 0}}
                        label={<Typography variant="body2">{t("participants.send_email.template_custom")}</Typography>}
                      />
                    )}
                  </RadioGroup>
                  {useCustomTemplate && templates.length > 0 && (
                    <Box>
                      <Typography color="neutral.dark" sx={{fontWeight: "bold"}} variant="body2">
                        {t("participants.send_email.template_label")}
                      </Typography>
                      <Autocomplete
                        options={templates}
                        getOptionLabel={(tpl): string => tpl.name}
                        value={selectedTemplate}
                        onChange={(_, val): void => setSelectedTemplate(val)}
                        disabled={loading}
                        renderInput={(params): JSX.Element => (
                          <TextField {...params} inputProps={{...params.inputProps, placeholder: t("participants.send_email.template_placeholder")}} sx={{"& input": {p: 3}}} />
                        )}
                      />
                    </Box>
                  )}
                </>
              )}
              {(!useCustomTemplate || selectedTemplate !== null) && (!manualIdsMode || parsedManualIds.length > 0) && (
                <Alert severity="info" sx={{border: "2px solid #A8C2EC", borderLeft: "4px solid #A8C2EC"}}>
                  {recipientInfoKey ? t(recipientInfoKey, {
                    template: useCustomTemplate && selectedTemplate ? selectedTemplate.name : t("participants.send_email.template_invitation"),
                    count: participantCount,
                  }) : t(operationParticipant ? "participants.send_email.recipient_info" : "participants.send_email.recipient_selected_participant", {
                    template: useCustomTemplate && selectedTemplate ? selectedTemplate.name : t("participants.send_email.template_invitation"),
                    name: `${operationParticipant?.participant.firstName} ${operationParticipant?.participant.lastName}`,
                    count: parsedManualIds.length > 0 ? parsedManualIds.length : participantCount,
                  })}
                </Alert>
              )}
              {manualIdsMode && (
                <Box>
                  <Typography variant="body2" sx={{pb: 1, fontWeight: "bold", color: "neutral.main"}}>
                    {t('participants.send_email.manual_ids_label')}
                  </Typography>
                  <TextField
                    multiline
                    minRows={3}
                    maxRows={6}
                    fullWidth
                    placeholder={t("participants.send_email.manual_ids_placeholder")}
                    value={manualIdsInput}
                    onChange={(e): void => setManualIdsInput(e.target.value)}
                    disabled={loading}
                  />
                  <Typography variant="caption">
                    {t("participants.send_email.manual_ids_helper")}
                  </Typography>
                  {duplicateCount > 0 && (
                    <Typography variant="caption" color="neutral.main" sx={{display: "block"}}>
                      {t("participants.send_email.manual_ids_duplicates", {count: duplicateCount})}
                    </Typography>
                  )}
                </Box>
              )}
              {invalidManualIds.length > 0 && (
                <Alert
                  severity="error"
                  sx={{
                    backgroundColor: "error.light",
                    border: "2px solid",
                    borderColor: "error.main",
                    borderLeft: "4px solid",
                    borderLeftColor: "error.main",
                    color: "common.black",
                    "& .MuiAlert-message": {color: "common.black"},
                  }}
                >
                  {t("participants.send_email.manual_ids_invalid", {
                    count: invalidManualIds.length,
                    ids: invalidManualIds.join(", "),
                  })}
                </Alert>
              )}
            </Box>
          )}

          <Box sx={{display: "flex", flexDirection: "column", gap: 2, mt: 2}}>
            <Box sx={{display: "flex", justifyContent: "space-between"}}>
              <Box>
                <Typography variant="body2" color="primary" sx={{fontWeight: "bold"}}>
                  {t("participants.send_email.schedule_title")}
                </Typography>
                <Typography variant="body2" color="neutral.main">
                  {t("participants.send_email.schedule_label")}
                </Typography>
              </Box>
              <Switch
                checked={scheduleEnabled}
                onChange={(e): void => setScheduleEnabled(e.target.checked)}
                disabled={loading}
                sx={{
                  "& .MuiSwitch-switchBase:not(.Mui-checked)": {
                    color: "grey.500",
                  },
                  "& .MuiSwitch-switchBase:not(.Mui-checked) + .MuiSwitch-track": {
                    bgcolor: "grey.400",
                    opacity: 1,
                  },
                }}
              />
            </Box>
            {scheduleEnabled && (
              <LocalizationProvider adapterLocale={fr} dateAdapter={AdapterDateFns}>
                <Box sx={{display: "flex", flexDirection: "column", gap: 2}}>
                  <Typography variant="body2" color="neutral.dark" sx={{fontWeight: "medium"}}>
                    {t("participants.send_email.schedule_helper")}
                  </Typography>
                  <DatePicker
                    inputFormat="dd/MM/yyyy"
                    value={sendAtDate}
                    disabled={loading}
                    minDate={new Date()}
                    onChange={(newDate: Date | null): void => { if (newDate) setSendAtDate(newDate); }}
                    renderInput={(params): any => (
                      <TextField {...params} inputProps={{...params.inputProps, placeholder: "jj/mm/aaaa"}} sx={{flex: 1, "& input": {p: 3}}} />
                    )}
                  />
                  <TimePicker
                    inputFormat="HH:mm"
                    value={sendAtTime}
                    disabled={loading}
                    minutesStep={5}
                    ampm={false}
                    onChange={(newTime: Date | null): void => { if (newTime) setSendAtTime(newTime); }}
                    renderInput={(params): any => (
                      <TextField {...params} inputProps={{...params.inputProps, placeholder: "hh:mm"}} sx={{flex: 1, "& input": {p: 3}}} />
                    )}
                  />
                </Box>
              </LocalizationProvider>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{p: 5, borderTop: "1px solid", borderColor: "ornament.dark"}}>
        <SecondaryBigButton sx={{mr: 0}} label={t("participants.send_email.cancel_button")} action={closeDialog} />
        <PrimaryBigButton action={onConfirm} sx={{height: "56px"}} label={scheduleEnabled ? t("participants.send_email.program_button") : t("participants.send_email.confirm_button")} loading={loading} disabled={(useCustomTemplate && selectedTemplate === null) || invalidManualIds.length > 0 || (manualIdsMode && parsedManualIds.length === 0)} />
      </DialogActions>
    </Dialog>
  );
}